import { useEffect, useRef, useState } from 'react';
import { StationCanvas } from '../components/station/StationCanvas';
import { Hud } from '../components/station/Hud';
import { RoomDetailPanel } from '../components/station/RoomDetailPanel';
import { KeyboardShadow } from '../components/station/KeyboardShadow';
import { RecordBar } from '../components/cinema/RecordBar';
import { useAgentStates } from '../hooks/useAgentStates';
import { useCinematicCamera } from '../hooks/useCinematicCamera';
import { useRecordingMode } from '../hooks/useRecordingMode';
import type { AgentId } from '../lib/schema';

export function Station() {
  const { states, lastSync } = useAgentStates();
  const [camera, dispatch] = useCinematicCamera();
  const [selected, setSelected] = useState<AgentId | null>(null);
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);

  const getCanvas = () =>
    canvasContainerRef.current?.querySelector('canvas') ?? null;

  const recording = useRecordingMode(getCanvas);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const key = e.key.toLowerCase();
      switch (key) {
        case 'r':
          if (recording.recording) recording.stop();
          else void recording.start();
          dispatch({ type: 'SET_MODE', mode: recording.recording ? 'free' : 'record' });
          break;
        case 'c':
          dispatch({ type: 'TOGGLE_CRT' });
          break;
        case 'h':
          dispatch({ type: 'TOGGLE_HUD' });
          break;
        case 'z':
          dispatch({ type: 'CYCLE_ZOOM' });
          break;
        case 't':
          dispatch({ type: 'TOGGLE_THEME' });
          break;
        case 'escape':
          setSelected(null);
          dispatch({ type: 'FOCUS', agentId: null });
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [recording, dispatch]);

  return (
    <main className="relative w-full h-screen overflow-hidden">
      {/* Ops-console dot grid backdrop */}
      <div className="dotgrid-bg" aria-hidden="true" />
      {/* Slow scan bar */}
      <div className="scan-bar" aria-hidden="true" />

      <div
        ref={canvasContainerRef}
        style={{ position: 'absolute', inset: 0, zIndex: 1 }}
      >
        <StationCanvas
          agentStates={states}
          camera={camera}
          onSelectAgent={(id) => {
            setSelected(id);
            dispatch({ type: 'FOCUS', agentId: id });
          }}
        />
      </div>

      {/* Film grain (above canvas, below HUD) */}
      <div className="grain" aria-hidden="true" />


      {camera.hudVisible && (
        <>
          <Hud states={states} lastSync={lastSync} theme={camera.theme} />
          <RecordBar camera={camera} dispatch={dispatch} recording={recording} />
        </>
      )}

      <KeyboardShadow
        states={states}
        onSelect={(id) => {
          setSelected(id);
          dispatch({ type: 'FOCUS', agentId: id });
        }}
      />

      {selected && (
        <RoomDetailPanel
          agentId={selected}
          state={states.find((s) => s.agentId === selected)!}
          onClose={() => {
            setSelected(null);
            dispatch({ type: 'FOCUS', agentId: null });
          }}
        />
      )}
    </main>
  );
}
